// The Forecast page (Planning; ERP-PLAN §5.4 "Forecasting"): every sell item's forecast with its method,
// trend, stockouts and accuracy (Items); the next 12 months and the planned purchases by month and vendor
// (Overview); stock value, overstock, dead stock and lost sales (Health); scoped adjustments and "forecast
// like" (Adjustments). All worked out in the browser from synced data (lib/forecast.ts), so nothing waits.
// ?tab= picks the tab, ?vendor= filters to a vendor's items, ?item= opens an item.
import { AlertTriangle, Trash2, TrendingUp } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { numberOf } from "../../../shared/calc.ts";
import { upcomingOneTime } from "../../../shared/costs.ts"; // Production runs ---
import type { ForecastAdjustment, Item, ItemPlan } from "../../../shared/db.gen.ts";
import { factorText, type HealthRow, type ItemForecast, monthIso, monthNum, quality, scopeLabel, staleText, sumAccuracy } from "../../../shared/forecast.ts";
import { ADJUSTMENT_SCOPE_LABELS, ADJUSTMENT_SCOPES } from "../../../shared/mutations-planning.ts";
import { fmt, monthLabel } from "../../../shared/planning.ts";
import { useCostIndex } from "../lib/costs.ts";
import { useForecasts, useHealth, usePlannedPurchases } from "../lib/forecast.ts";
import { can, mutate } from "../lib/mutate.ts";
import { usePlan } from "../lib/planning.ts";
import { Link, setSearch, useSearch } from "../lib/router.tsx";
import { useRow, useRows } from "../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Input, Select, Thumb } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { PageSearch, useDetailKeys } from "../ui/ListPage.tsx";
import { toast } from "../ui/toast.tsx";
import { ForecastSection, methodChip, QUALITY_LABEL, QUALITY_TONE } from "./ForecastSection.tsx";
import { ItemPlanSection } from "./ItemPlanSection.tsx";
import { SalesSection } from "./ItemSales.tsx";
import { PlanDetail } from "./PlanDetail.tsx";

const TABS = ["items", "overview", "health", "adjustments"] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = { items: "Items", overview: "Overview", health: "Health", adjustments: "Adjustments" };

/** $1,234 / $12.9K / $1.2M. */
const usd = (n: number) =>
  `${n < 0 ? "−" : ""}$${Math.abs(n) >= 1e6 ? `${fmt(Math.round(Math.abs(n) / 1e5) / 10)}M` : Math.abs(n) >= 1e4 ? `${fmt(Math.round(Math.abs(n) / 100) / 10)}K` : fmt(Math.round(Math.abs(n)))}`;
const pct = (n: number | null) => (n === null ? "—" : `${fmt(Math.round(n * 100))}%`);

/** Each item's preferred (else first) vendor. */
function useVendorOf() {
  const links = useRows("vendor_item");
  return useMemo(() => {
    const out = new Map<string, string>();
    for (const l of [...links].sort((a, b) => Number(b.is_preferred) - Number(a.is_preferred) || a.created_at.localeCompare(b.created_at))) {
      if (!out.has(l.item_id)) out.set(l.item_id, l.vendor_id);
    }
    return out;
  }, [links]);
}

export function ForecastPage() {
  const params = useSearch();
  const tabParam = params.get("tab") as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : "items";
  const vendorFilter = params.get("vendor") ?? "";
  const openId = params.get("item");
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  // Table views --- the Items table's View and Columns menus sit by the title, as on every list page
  const [viewsSlot, setViewsSlot] = useState<HTMLDivElement | null>(null);
  const vendors = useRows("vendor");
  const bases = useRows("forecast_base");
  const stale = staleText(useRows("forecast_run")[0]);
  const vendorOf = useVendorOf();
  const vendorOptions = useMemo(() => {
    const used = new Set(vendorOf.values());
    return [
      { value: "", label: "All vendors" },
      ...vendors
        .filter((v) => used.has(v.id))
        .sort((a, b) => (a.short_name || a.name).localeCompare(b.short_name || b.name))
        .map((v) => ({ value: v.id, label: v.short_name || v.name })),
    ];
  }, [vendors, vendorOf]);
  // Stable between renders, so the tabs' memos only rerun when the filter or the links change.
  const inVendor = useCallback((id: string) => !vendorFilter || vendorOf.get(id) === vendorFilter, [vendorFilter, vendorOf]);

  useDetailKeys(searchRef, !!openId);

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
          <h1 className="text-[15px] font-semibold whitespace-nowrap">Forecast</h1>
          {tab === "items" && <div ref={setViewsSlot} className="-ml-1 flex shrink-0 items-center gap-1" />}
          {tab === "items" && <PageSearch ref={searchRef} value={query} onChange={setQuery} placeholder="Filter by SKU, name…" toTable className="ml-2 w-60" />}
          {tab !== "adjustments" && (
            <div className="w-44">
              <Select value={vendorFilter} options={vendorOptions} onChange={(e) => setSearch({ vendor: e.target.value || null })} />
            </div>
          )}
          <Link to="/purchasing" className="ml-auto text-[12px] text-accent-text hover:underline">
            Purchasing plan
          </Link>
        </header>
        <nav className="flex shrink-0 gap-1 border-b border-line bg-surface px-3">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setSearch({ tab: t === "items" ? null : t })}
              className={cx(
                "-mb-px border-b-2 px-2 py-2 text-[12px] whitespace-nowrap",
                tab === t ? "border-accent text-text" : "border-transparent text-muted hover:text-text",
              )}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </nav>
        {!bases.length && (
          <div className="flex items-center gap-2 border-b border-line bg-warn-soft px-4 py-2 text-[12px] text-warn">
            <AlertTriangle className="size-4" /> The nightly forecast hasn't run yet, so these are raw sales rates: no stock history, seasonality or trend.
            Settings → Shopify → Stock history, then Forecast.
          </div>
        )}
        {stale && (
          <div className="flex items-center gap-2 border-b border-line bg-warn-soft px-4 py-2 text-[12px] text-warn">
            <AlertTriangle className="size-4" /> {stale}. Check the Stock history pull in{" "}
            <Link to="/settings/shopify" className="underline">
              Settings → Shopify
            </Link>
            .
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto bg-surface">
          {tab === "items" && <ItemsTab query={query} openId={openId} inVendor={inVendor} vendorOf={vendorOf} viewsSlot={viewsSlot} />}
          {tab === "overview" && <OverviewTab inVendor={inVendor} />}
          {tab === "health" && <HealthTab inVendor={inVendor} />}
          {tab === "adjustments" && <AdjustmentsTab />}
        </div>
      </div>
      {openId && <ForecastDetail id={openId} />}
    </div>
  );
}

// Items ------------------------------------------------------------------------------------------------------

type ItemRow = {
  id: string;
  item: Item;
  f: ItemForecast;
  vendor: string;
  health: HealthRow | undefined;
};

function ItemsTab({
  query,
  openId,
  inVendor,
  vendorOf,
  viewsSlot,
}: {
  query: string;
  openId: string | null;
  inVendor: (id: string) => boolean;
  vendorOf: Map<string, string>;
  viewsSlot: HTMLElement | null;
}) {
  const forecasts = useForecasts();
  const health = useHealth();
  const items = useRows("item");
  const vendors = useRows("vendor");
  const rows = useMemo(() => {
    const healthOf = new Map(health.rows.map((r) => [r.item.id, r]));
    const vendorName = new Map(vendors.map((v) => [v.id, v.short_name || v.name]));
    return items.flatMap((item): ItemRow[] => {
      const f = forecasts.byItem.get(item.id);
      if (!f || !inVendor(item.id)) return [];
      return [{ id: item.id, item, f, vendor: vendorName.get(vendorOf.get(item.id) ?? "") ?? "", health: healthOf.get(item.id) }];
    });
  }, [items, forecasts, health, vendors, vendorOf, inVendor]);
  const columns = useMemo<Column<ItemRow>[]>(
    () => [
      {
        key: "sku",
        header: "SKU",
        width: "150px",
        value: (r) => r.item.sku,
        cell: (r) => (
          <span className="flex min-w-0 items-center gap-2">
            <Thumb src={r.item.photo_url} size={22} />
            <span className="truncate font-mono text-[12px] font-medium">{r.item.sku || "No SKU"}</span>
          </span>
        ),
      },
      { key: "name", header: "Name", width: "minmax(200px,2fr)", value: (r) => r.item.name, cell: (r) => <span className="truncate">{r.item.name}</span> },
      { key: "vendor", header: "Vendor", width: "110px", cell: (r) => <span className="truncate text-muted">{r.vendor}</span> },
      {
        key: "method",
        header: "Method",
        width: "96px",
        value: (r) => methodChip(r.f).label,
        cell: (r) => {
          const c = methodChip(r.f);
          return (
            <span title={c.hint}>
              <Badge tone={c.tone}>{c.label}</Badge>
            </span>
          );
        },
      },
      {
        key: "for_sale",
        header: "For sale",
        headerTitle:
          "Available at HHL (on hand less units committed to customer orders) less units held for pre-sales: what cover, overstock and dead stock count",
        width: "76px",
        align: "right",
        value: (r) => r.health?.available ?? 0,
        cell: (r) =>
          r.health ? (
            <span title={r.health.held > 0 ? `${fmt(r.health.held)} more held for pre-sales` : undefined}>{fmt(r.health.available)}</span>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        key: "cover",
        header: "Cover",
        width: "68px",
        align: "right",
        // Days the stock at HHL lasts at the forecast; no forecast sales sorts last.
        value: (r) => r.health?.coverDays ?? Number.POSITIVE_INFINITY,
        text: (r) => (r.health?.coverDays == null ? "" : Math.round(r.health.coverDays)),
        cell: (r) => {
          const d = r.health?.coverDays;
          if (d == null) return <span className="text-faint">—</span>;
          return <span className={cx(d < 14 && "text-danger", d >= 14 && d < 30 && "text-warn")}>{d >= 999 ? "999d+" : `${fmt(Math.round(d))}d`}</span>;
        },
      },
      { key: "next30", header: "30 days", width: "72px", align: "right", value: (r) => r.f.next30, cell: (r) => fmt(Math.round(r.f.next30)) },
      { key: "next90", header: "90 days", width: "72px", align: "right", value: (r) => r.f.next90, cell: (r) => fmt(Math.round(r.f.next90)) },
      { key: "next365", header: "12 months", width: "84px", align: "right", value: (r) => r.f.next365, cell: (r) => fmt(Math.round(r.f.next365)) },
      { key: "level", header: "Base/day", width: "76px", align: "right", value: (r) => r.f.level, cell: (r) => fmt(r.f.level) },
      {
        key: "trend",
        header: "Trend",
        width: "64px",
        align: "right",
        value: (r) => r.f.trend,
        cell: (r) => (r.f.trend === 1 ? <span className="text-faint">—</span> : factorText(r.f.trend)),
      },
      {
        key: "out90",
        header: "Out 90d",
        width: "68px",
        align: "right",
        value: (r) => r.f.base?.out_90 ?? 0,
        cell: (r) => (r.f.base?.out_90 ? `${r.f.base.out_90}d` : <span className="text-faint">—</span>),
      },
      {
        key: "lost90",
        header: "Lost 90d",
        width: "76px",
        align: "right",
        value: (r) => r.health?.lostRevenue90 ?? 0,
        cell: (r) => (r.health?.lostRevenue90 ? usd(r.health.lostRevenue90) : <span className="text-faint">—</span>),
      },
      {
        key: "accuracy",
        header: "Accuracy",
        width: "80px",
        value: (r) => quality(r.f.base).wape ?? 9,
        text: (r) => QUALITY_LABEL[quality(r.f.base).label],
        cell: (r) => {
          const q = quality(r.f.base);
          return (
            <span title={q.text}>
              <Badge tone={QUALITY_TONE[q.label]}>{QUALITY_LABEL[q.label]}</Badge>
            </span>
          );
        },
      },
    ],
    [],
  );
  return (
    <div className="h-full">
      <DataTable
        rows={rows}
        columns={columns}
        search={query}
        activeId={openId}
        defaultSort={{ key: "next365", desc: true }}
        onOpen={(r) => setSearch({ item: r.id })}
        rowNoun={["item", "items"]}
        exportName="Forecast"
        viewsSlot={viewsSlot}
        empty={<EmptyState icon={<TrendingUp className="size-8" />} title={query ? "Nothing matches" : "No sell items to forecast"} />}
      />
    </div>
  );
}

// Overview ---------------------------------------------------------------------------------------------------

function OverviewTab({ inVendor }: { inVendor: (id: string) => boolean }) {
  const forecasts = useForecasts();
  const plan = usePlan();
  const { orders, stale } = usePlannedPurchases(plan);
  const items = useRows("item");
  const projects = useRows("project");
  const vendors = useRows("vendor");
  // Production runs --- the one-time costs of runs still to come (tooling they pay for, unpaid one-time entries), by date.
  const cxRuns = useCostIndex();
  const vendorFilter = useSearch().get("vendor");
  const oneTime = useMemo(() => (vendorFilter ? [] : upcomingOneTime(cxRuns)), [cxRuns, vendorFilter]);
  const [projectsSlot, setProjectsSlot] = useState<HTMLDivElement | null>(null); // Table views --- By project's, by its title
  // The forecast's totals (a change of stock doesn't touch these) …
  const fc = useMemo(() => {
    const itemOf = new Map(items.map((i) => [i.id, i]));
    const months = Array.from({ length: 12 }, (_, k) => monthIso(forecasts.month + k));
    const units = new Array(12).fill(0);
    const revenue = new Array(12).fill(0);
    let next30 = 0;
    let next365 = 0;
    let revenue365 = 0;
    const byProject = new Map<string, { next30: number; next90: number; next365: number; items: number }>();
    const mine: ItemForecast[] = [];
    for (const f of forecasts.byItem.values()) {
      if (!inVendor(f.itemId)) continue;
      mine.push(f);
      const item = itemOf.get(f.itemId);
      const price = Number(item?.price ?? 0);
      f.months.forEach((m, k) => {
        units[k] += m.units;
        revenue[k] += m.units * price;
      });
      next30 += f.next30;
      next365 += f.next365;
      revenue365 += f.next365 * price;
      const project = item?.project_id ?? "";
      const p = byProject.get(project) ?? { next30: 0, next90: 0, next365: 0, items: 0 };
      p.next30 += f.next30;
      p.next90 += f.next90;
      p.next365 += f.next365;
      p.items++;
      byProject.set(project, p);
    }
    return { months, units, revenue, next30, next365, revenue365, byProject, accuracy: sumAccuracy(mine.map((f) => f.base)) };
  }, [forecasts, items, inVendor]);
  // … and the planned purchases: USD by month and vendor; the biggest five vendors keep their own colour.
  const spend = useMemo(() => {
    const byVendor = new Map<string, number[]>();
    let other = 0;
    for (const o of orders ?? []) {
      if (!inVendor(o.result.item.id) || o.cost == null) continue;
      if (o.currency !== "USD") {
        other++;
        continue;
      }
      const k = monthNum(o.month) - forecasts.month;
      if (k < 0 || k > 11) continue;
      const key = o.result.vendor?.id ?? "none";
      const row = byVendor.get(key) ?? new Array(12).fill(0);
      row[k] += o.cost;
      byVendor.set(key, row);
    }
    for (const c of oneTime) {
      const k = monthNum(`${c.date.slice(0, 7)}-01`) - forecasts.month;
      if (k < 0 || k > 11) continue;
      const row = byVendor.get("runs") ?? new Array(12).fill(0);
      row[Math.max(0, k)] += c.usd;
      byVendor.set("runs", row);
    }
    const vendorOf = new Map(vendors.map((v) => [v.id, v.short_name || v.name]));
    const vendorName = (id: string) => (id === "none" ? "No vendor" : id === "runs" ? "One-time costs of upcoming production runs" : (vendorOf.get(id) ?? "?"));
    const ranked = [...byVendor].sort((a, b) => b[1].reduce((x, y) => x + y, 0) - a[1].reduce((x, y) => x + y, 0));
    const top = ranked.slice(0, 5).map(([id, values]) => ({ key: id, label: vendorName(id), values }));
    const rest = ranked.slice(5);
    if (rest.length)
      top.push({
        key: "other",
        label: `${rest.length} other vendor${rest.length === 1 ? "" : "s"}`,
        values: Array.from({ length: 12 }, (_, k) => rest.reduce((n, [, v]) => n + v[k], 0)),
      });
    const spend12 = ranked.reduce((n, [, v]) => n + v.reduce((x, y) => x + y, 0), 0);
    return { spendSeries: top, ranked, vendorName, spend12, otherCurrency: other };
  }, [orders, forecasts.month, vendors, inVendor, oneTime]);
  const data = { ...fc, ...spend };
  const q = quality(data.accuracy);
  const projectName = (id: string) => (id ? (projects.find((p) => p.id === id)?.name ?? "?") : "No project"); // a few rows

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Tile label="Next 30 days" value={fmt(Math.round(data.next30))} sub="units" />
        <Tile label="Next 12 months" value={fmt(Math.round(data.next365))} sub="units" />
        <Tile label="Revenue, 12 months" value={usd(data.revenue365)} sub="at today's prices" />
        <Tile
          label="Planned purchases, 12 months"
          value={orders ? usd(data.spend12) : "…"}
          sub={data.otherCurrency ? `USD; ${data.otherCurrency} orders in other currencies` : "USD, by order date"}
        />
        <Tile
          label="Accuracy"
          value={QUALITY_LABEL[q.label]}
          sub={
            q.wape === null
              ? q.text
              : `off ${pct(q.wape)} a month, ${q.bias !== null && Math.abs(q.bias) >= 0.05 ? `${pct(Math.abs(q.bias))} ${q.bias > 0 ? "high" : "low"}` : "no bias"}`
          }
          tone={QUALITY_TONE[q.label]}
        />
      </div>
      <ChartCard title="Forecast by month" subtitle="Units, overrides and adjustments included (B2B orders not)">
        <MonthColumns
          months={data.months}
          series={[{ key: "units", label: "Units", values: data.units }]}
          format={(n) => fmt(Math.round(n))}
          extra={(k) => `${usd(data.revenue[k])} at today's prices`}
        />
      </ChartCard>
      <ChartCard
        title="Planned purchases by month"
        subtitle="What the plan would order, by order date and vendor (USD): lead times, MOQs and order cycles simulated over the year, plus the one-time costs (tooling, unpaid one-time invoices) of production runs still planning or ordering, in the month of their invoice or target ship date"
      >
        {!orders ? (
          <div className="p-6 text-center text-muted">Working out the year's orders…</div>
        ) : data.spendSeries.length ? (
          // While a newer plan is being worked out, the last one stays, faded.
          <div className={cx("transition-opacity", stale && "opacity-60")}>
            <MonthColumns months={data.months} series={data.spendSeries} format={usd} />
            <SpendTable months={data.months} ranked={data.ranked} vendorName={data.vendorName} />
          </div>
        ) : (
          <div className="p-6 text-center text-muted">Nothing to buy in the next 12 months.</div>
        )}
      </ChartCard>
      <ChartCard title="By project" subtitle="Units forecast" views={setProjectsSlot}>
        <DataTable
          fit
          rows={[...data.byProject].map(([id, p]) => ({ id: id || "none", name: projectName(id), ...p }))}
          columns={PROJECT_COLUMNS}
          defaultSort={{ key: "next365", desc: true }}
          rowNoun={["project", "projects"]}
          exportName="By project"
          viewsSlot={projectsSlot}
          pageFilters={false}
        />
      </ChartCard>
    </div>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "ok" | "warn" | "danger" | "neutral" }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2.5">
      <div className="text-[11px] text-muted">{label}</div>
      <div className={cx("text-[20px] font-semibold", tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : tone === "ok" ? "text-ok" : "")}>
        {value}
      </div>
      {sub && (
        <div className="truncate text-[11px] text-faint" title={sub}>
          {sub}
        </div>
      )}
    </div>
  );
}

/** A card with a title; `views` is the ref of the place beside the title for its table's View and Columns menus. */
function ChartCard({
  title,
  subtitle,
  views,
  children,
}: {
  title: string;
  subtitle?: string;
  views?: (el: HTMLDivElement | null) => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-[13px] font-semibold">{title}</h2>
        {views && <div ref={views} className="ml-auto flex items-center gap-1" />}
      </div>
      {subtitle && <div className="mb-3 text-[12px] text-muted">{subtitle}</div>}
      {children}
    </section>
  );
}

type ProjectRow = { id: string; name: string; items: number; next30: number; next90: number; next365: number };
const units = (n: number) => fmt(Math.round(n));
const PROJECT_COLUMNS: Column<ProjectRow>[] = [
  { key: "name", header: "Project", width: "minmax(160px,2fr)" },
  { key: "items", header: "Items", width: "90px", align: "right", cell: (p) => <span className="text-muted">{fmt(p.items)}</span> },
  { key: "next30", header: "30 days", width: "100px", align: "right", value: (p) => Math.round(p.next30), cell: (p) => units(p.next30) },
  { key: "next90", header: "90 days", width: "100px", align: "right", value: (p) => Math.round(p.next90), cell: (p) => units(p.next90) },
  { key: "next365", header: "12 months", width: "100px", align: "right", value: (p) => Math.round(p.next365), cell: (p) => units(p.next365) },
];

const SERIES = ["var(--series-1)", "var(--series-2)", "var(--series-3)", "var(--series-4)", "var(--series-5)", "var(--series-6)"];

/** 12 monthly columns, stacked when there are several series (each keeps its colour by position). */
function MonthColumns({
  months,
  series,
  format,
  extra,
}: {
  months: string[];
  series: { key: string; label: string; values: number[] }[];
  format: (n: number) => string;
  extra?: (k: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 200;
  const pad = { left: 44, right: 8, top: 10, bottom: 22 };
  const totals = months.map((_, k) => series.reduce((n, s) => n + Math.max(0, s.values[k]), 0));
  const max = Math.max(1, ...totals);
  const step = (() => {
    const raw = max / 4;
    const pow = 10 ** Math.floor(Math.log10(raw));
    return [1, 2, 2.5, 5, 10].map((s) => s * pow).find((s) => s >= raw) ?? raw;
  })();
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;
  const slot = plotW / months.length;
  const bar = Math.min(24, slot - 2);
  const y = (v: number) => pad.top + plotH - (v / top) * plotH;
  return (
    <div className="relative">
      {series.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-3 text-[11px] text-muted">
          {series.map((s, i) => (
            <span key={s.key} className="flex items-center gap-1">
              <span className="inline-block size-2.5 rounded-sm" style={{ background: SERIES[i] }} /> {s.label}
            </span>
          ))}
        </div>
      )}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full"
        role="img"
        aria-label={months.map((m, k) => `${monthLabel(m)}: ${format(totals[k])}`).join("; ")}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} stroke="var(--line)" strokeWidth={1} />
            <text x={pad.left - 6} y={y(t) + 3} textAnchor="end" className="fill-[var(--faint)] text-[10px] tabular-nums">
              {format(t)}
            </text>
          </g>
        ))}
        {months.map((m, k) => {
          let base = 0;
          const x = pad.left + k * slot + (slot - bar) / 2;
          const segments = series.map((s, i) => {
            const v = Math.max(0, s.values[k]);
            const y0 = y(base);
            base += v;
            const y1 = y(base);
            // A 2px surface gap between stacked segments.
            const h = Math.max(0, y0 - y1 - (i > 0 ? 2 : 0));
            const topMost = series.slice(i + 1).every((t) => Math.max(0, t.values[k]) === 0);
            const r = topMost ? Math.min(3, h, bar / 2) : 0;
            return h > 0 ? (
              <path
                key={s.key}
                d={`M${x},${y1 + h}V${y1 + r}${r ? `Q${x},${y1} ${x + r},${y1}H${x + bar - r}Q${x + bar},${y1} ${x + bar},${y1 + r}` : `H${x + bar}`}V${y1 + h}Z`}
                fill={SERIES[i]}
                opacity={hover === null || hover === k ? 1 : 0.55}
              />
            ) : null;
          });
          return (
            <g key={m}>
              {segments}
              <text x={pad.left + k * slot + slot / 2} y={H - 6} textAnchor="middle" className="fill-[var(--muted)] text-[10px]">
                {monthLabel(m).slice(0, 3)}
                {m.slice(5, 7) === "01" || k === 0 ? ` ${m.slice(2, 4)}` : ""}
              </text>
              <rect x={pad.left + k * slot} y={0} width={slot} height={H} fill="transparent" onMouseEnter={() => setHover(k)} />
            </g>
          );
        })}
        <line x1={pad.left} x2={W - pad.right} y1={y(0)} y2={y(0)} stroke="var(--line-strong)" strokeWidth={1} />
      </svg>
      {hover !== null && (
        <div
          className="pointer-events-none absolute top-8 z-10 w-60 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[11px] shadow-pop"
          style={hover > months.length / 2 ? { left: 48 } : { right: 8 }}
        >
          <div className="font-medium">{monthLabel(months[hover])}</div>
          {series.map((s, i) => (
            <div key={s.key} className="flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-2.5" style={{ background: SERIES[i] }} />
              <span className="font-semibold">{format(s.values[hover])}</span>
              {series.length > 1 && <span className="truncate text-muted">{s.label}</span>}
            </div>
          ))}
          {series.length > 1 && <div className="mt-0.5 border-t border-line pt-0.5 font-semibold">{format(totals[hover])} in all</div>}
          {extra && <div className="text-muted">{extra(hover)}</div>}
        </div>
      )}
    </div>
  );
}

function SpendTable({ months, ranked, vendorName }: { months: string[]; ranked: [string, number[]][]; vendorName: (id: string) => string }) {
  const totals = months.map((_, k) => ranked.reduce((n, [, values]) => n + values[k], 0));
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead className="text-faint">
          <tr>
            <th className="py-1 pr-2 text-left font-medium">Vendor</th>
            {months.map((m) => (
              <th key={m} className="px-1 py-1 text-right font-medium">
                {monthLabel(m).slice(0, 3)}
              </th>
            ))}
            <th className="py-1 pl-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {ranked.map(([id, values]) => (
            <tr key={id} className="border-t border-line/60">
              <td className="max-w-40 truncate py-1 pr-2">
                {id === "none" ? (
                  vendorName(id)
                ) : (
                  <Link to={`/purchasing?vendor=${id}&bucket=all`} className="hover:underline">
                    {vendorName(id)}
                  </Link>
                )}
              </td>
              {values.map((v, k) => (
                <td key={months[k]} className={cx("px-1 py-1 text-right", !v && "text-faint")}>
                  {v ? usd(v) : "—"}
                </td>
              ))}
              <td className="py-1 pl-2 text-right font-medium">{usd(values.reduce((a, b) => a + b, 0))}</td>
            </tr>
          ))}
        </tbody>
        {ranked.length > 1 && (
          <tfoot className="tabular-nums">
            <tr className="border-t border-line-strong font-semibold">
              <td className="py-1 pr-2">All vendors</td>
              {totals.map((v, k) => (
                <td key={months[k]} className={cx("px-1 py-1 text-right", !v && "font-normal text-faint")}>
                  {v ? usd(v) : "—"}
                </td>
              ))}
              <td className="py-1 pl-2 text-right">{usd(totals.reduce((a, b) => a + b, 0))}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// Health -------------------------------------------------------------------------------------------------------

function HealthTab({ inVendor }: { inVendor: (id: string) => boolean }) {
  const health = useHealth();
  const settings = useRows("planning_settings")[0];
  const rows = useMemo(() => health.rows.filter((r) => inVendor(r.item.id)), [health, inVendor]);
  const sum = (pick: (r: HealthRow) => number, only: (r: HealthRow) => boolean = () => true) => rows.reduce((n, r) => (only(r) ? n + pick(r) : n), 0);
  const sold = sum((r) => r.sold90);
  const stock = sum((r) => r.available);
  const months = Number(settings?.overstock_months ?? 12);
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Tile label="Stock value" value={usd(sum((r) => r.value))} sub={`${fmt(stock)} units for sale, at cost`} />
        <Tile label="Overstock" value={usd(sum((r) => r.overstockValue))} sub={`beyond ${fmt(months)} months of cover`} />
        <Tile
          label="Dead 90 days"
          value={usd(
            sum(
              (r) => r.value,
              (r) => r.dead90,
            ),
          )}
          sub={`${fmt(rows.filter((r) => r.dead90).length)} items, no sales`}
        />
        <Tile
          label="Dead 180 days"
          value={usd(
            sum(
              (r) => r.value,
              (r) => r.dead180,
            ),
          )}
          sub={`${fmt(rows.filter((r) => r.dead180).length)} items`}
        />
        <Tile label="Lost, 30 days" value={usd(sum((r) => r.lostRevenue30))} sub={`${fmt(rows.filter((r) => r.out30 > 0).length)} items out of stock`} />
        <Tile label="Lost, 90 days" value={usd(sum((r) => r.lostRevenue90))} sub={`${fmt(rows.filter((r) => r.out90 > 0).length)} items out of stock`} />
        <Tile label="Sell-through, 90 days" value={sold + stock ? pct(sold / (sold + stock)) : "—"} sub="sold ÷ (sold + for sale)" />
      </div>
      {rows.some((r) => r.available > 0 && r.unitCost == null) && (
        <div className="text-[12px] text-muted">
          {fmt(rows.filter((r) => r.available > 0 && r.unitCost == null).length)} items with stock have no cost (no standard cost and no USD vendor price), so
          they count as $0.
        </div>
      )}
      <HealthList
        title="Overstock"
        subtitle={`Stock beyond ${fmt(months)} months of the forecast, at cost`}
        rows={rows
          .filter((r) => r.overstockUnits > 0 && r.available > 0)
          .sort((a, b) => b.overstockValue - a.overstockValue || b.overstockUnits - a.overstockUnits)}
        sort="extra_usd"
        columns={[
          forSale,
          {
            key: "cover",
            header: "Cover",
            width: "84px",
            align: "right",
            value: (r) => r.coverDays,
            text: (r) => (r.coverDays === null ? "no sales" : Math.round(r.coverDays / 30.44)),
            cell: (r) => (r.coverDays === null ? "no sales" : `${fmt(Math.round(r.coverDays / 30.44))} mo`),
          },
          {
            key: "extra",
            header: "Extra units",
            width: "96px",
            align: "right",
            value: (r) => Math.round(r.overstockUnits),
            cell: (r) => fmt(Math.round(r.overstockUnits)),
          },
          {
            key: "extra_usd",
            header: "Extra $",
            width: "90px",
            align: "right",
            value: (r) => r.overstockValue,
            text: (r) => (r.unitCost == null ? "no cost" : r.overstockValue),
            cell: (r) => (r.unitCost == null ? "no cost" : usd(r.overstockValue)),
          },
        ]}
      />
      <HealthList
        title="Dead stock"
        subtitle="Stock with no sales in 90 days (180+ marked)"
        rows={rows.filter((r) => r.dead90).sort((a, b) => b.value - a.value || b.available - a.available)}
        sort="value"
        columns={[
          forSale,
          {
            key: "value",
            header: "Value",
            width: "90px",
            align: "right",
            value: (r) => r.value,
            text: (r) => (r.unitCost == null ? "no cost" : r.value),
            cell: (r) => (r.unitCost == null ? "no cost" : usd(r.value)),
          },
          {
            key: "last_sold",
            header: "Last sold",
            width: "120px",
            align: "right",
            value: (r) => r.lastSold ?? "",
            text: (r) => (r.lastSold ? `${r.lastSold}${r.dead180 ? " (180+)" : ""}` : "never"),
            cell: (r) => (r.lastSold ? `${r.lastSold}${r.dead180 ? " (180+)" : ""}` : "never"),
          },
        ]}
      />
      <HealthList
        title="Stockouts"
        subtitle="Days out of stock at HHL and the sales they cost (days out × the base rate × price)"
        rows={rows.filter((r) => r.out90 > 0).sort((a, b) => b.lostRevenue90 - a.lostRevenue90 || b.out90 - a.out90)}
        sort="lost_usd"
        columns={[
          { key: "out30", header: "Out 30d", width: "80px", align: "right", value: (r) => r.out30, cell: (r) => `${r.out30}d` },
          { key: "out90", header: "Out 90d", width: "80px", align: "right", value: (r) => r.out90, cell: (r) => `${r.out90}d` },
          { key: "lost", header: "Lost units 90d", width: "110px", align: "right", value: (r) => Math.round(r.lost90), cell: (r) => fmt(Math.round(r.lost90)) },
          { key: "lost_usd", header: "Lost 90d", width: "90px", align: "right", value: (r) => r.lostRevenue90, cell: (r) => usd(r.lostRevenue90) },
        ]}
      />
    </div>
  );
}

type HealthItem = HealthRow & { id: string };
const forSale: Column<HealthItem> = { key: "available", header: "For sale", width: "80px", align: "right", cell: (r) => fmt(r.available) };

/** One of Health's lists, in the usual table (views, columns, Copy / CSV): the item, then its numbers; a row opens it. */
function HealthList({
  title,
  subtitle,
  rows,
  columns,
  sort,
}: {
  title: string;
  subtitle: string;
  rows: HealthRow[];
  columns: Column<HealthItem>[];
  /** The column it's sorted by first, biggest first (the rows come in that order, ties settled). */
  sort: string;
}) {
  const openId = useSearch().get("item");
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the card's title
  const items = useMemo(() => rows.map((r) => ({ ...r, id: r.item.id })), [rows]);
  const all: Column<HealthItem>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(130px,1fr)",
      value: (r) => r.item.sku,
      cell: (r) => (
        <>
          <Thumb src={r.item.photo_url} size={20} />
          <span className="truncate font-mono text-[12px]">{r.item.sku}</span>
        </>
      ),
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(160px,2fr)",
      value: (r) => r.item.name,
      cell: (r) => (
        <span className="truncate text-muted" title={r.item.name}>
          {r.item.name}
        </span>
      ),
    },
    ...columns,
  ];
  return (
    <ChartCard title={`${title} · ${fmt(rows.length)}`} subtitle={subtitle} views={rows.length ? setSlot : undefined}>
      {!rows.length ? (
        <div className="py-2 text-muted">None.</div>
      ) : (
        <DataTable
          fit={15}
          rows={items}
          columns={all}
          defaultSort={{ key: sort, desc: true }}
          activeId={openId}
          onOpen={(r) => setSearch({ item: r.item.id })}
          rowNoun={["item", "items"]}
          exportName={title}
          viewsSlot={slot}
          pageFilters={false}
        />
      )}
    </ChartCard>
  );
}

// Adjustments ------------------------------------------------------------------------------------------------

const monthInput = (iso: string | null) => (iso ? iso.slice(0, 7) : "");

function AdjustmentsTab() {
  const adjustments = useRows("forecast_adjustment");
  const forecasts = useForecasts();
  const items = useRows("item");
  const vendors = useRows("vendor");
  const projects = useRows("project");
  const plans = useRows("item_plan");
  const editable = can("forecast_adjustment.create");
  const affected = useMemo(() => {
    const n = new Map<string, number>();
    for (const f of forecasts.byItem.values()) for (const a of f.adjustments) n.set(a.id, (n.get(a.id) ?? 0) + 1);
    return n;
  }, [forecasts]);
  const labels = { items, vendors, projects };
  const likes = plans.filter((p) => p.like_item_id);
  const itemOf = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const update = (id: string, patch: Record<string, unknown>) => mutate("forecast_adjustment.update", { id, patch } as never);
  const openId = useSearch().get("item");
  // Table views --- each card's table has its menus by the card's title
  const [adjustSlot, setAdjustSlot] = useState<HTMLDivElement | null>(null);
  const [likeSlot, setLikeSlot] = useState<HTMLDivElement | null>(null);
  // Oldest first; the On column then puts the ones on first
  const ordered = useMemo(() => [...adjustments].sort((a, b) => a.created_at.localeCompare(b.created_at)), [adjustments]);
  const off = (a: ForecastAdjustment, className?: string) => cx(className, !a.active && "opacity-60");
  // A month typed as 2026-12 (empty: no limit), kept as its first day
  const month = (field: "month_from" | "month_to", label: string) => ({
    can: () => editable,
    get: (a: ForecastAdjustment) => monthInput(a[field]),
    save: (a: ForecastAdjustment, text: string) => {
      const t = text.trim().slice(0, 7);
      if (t && !/^\d{4}-\d{2}$/.test(t)) return toast.error(`${label}: use a month like 2026-12`);
      update(a.id, { [field]: t ? `${t}-01` : null });
    },
  });
  const adjustColumns: Column<ForecastAdjustment>[] = [
    {
      key: "scope",
      header: "Applies to",
      width: "minmax(160px,2fr)",
      value: (a) => scopeLabel(a, labels),
      cell: (a) => <span className={off(a, "truncate")}>{scopeLabel(a, labels)}</span>,
    },
    {
      key: "pct",
      header: "Change",
      width: "90px",
      align: "right",
      value: (a) => Number(a.pct),
      cell: (a) => <span className={off(a, "font-mono")}>{a.pct}%</span>,
      edit: {
        can: () => editable,
        get: (a) => String(a.pct),
        save: (a, t) => {
          const n = Number(t.replace(/[%+\s]/g, "").replace("−", "-"));
          if (!Number.isFinite(n) || n <= -100) return toast.error("Use a percentage above −100");
          update(a.id, { pct: n });
        },
      },
    },
    {
      key: "month_from",
      header: "From",
      headerTitle: "The first month it applies to (empty: from now)",
      width: "100px",
      value: (a) => a.month_from ?? "",
      text: (a) => monthInput(a.month_from),
      cell: (a) => <span className={off(a)}>{a.month_from ? monthLabel(a.month_from) : ""}</span>,
      edit: month("month_from", "From"),
    },
    {
      key: "month_to",
      header: "To",
      headerTitle: "The last month it applies to (empty: no end)",
      width: "100px",
      value: (a) => a.month_to ?? "",
      text: (a) => monthInput(a.month_to),
      cell: (a) => <span className={off(a)}>{a.month_to ? monthLabel(a.month_to) : ""}</span>,
      edit: month("month_to", "To"),
    },
    {
      key: "note",
      header: "Note",
      headerTitle: "Why",
      width: "minmax(140px,2fr)",
      cell: (a) => (
        <span className={off(a, "truncate text-muted")} title={a.note}>
          {a.note}
        </span>
      ),
      edit: { can: () => editable, get: (a) => a.note, save: (a, t) => update(a.id, { note: t }) },
    },
    {
      key: "items",
      header: "Items",
      headerTitle: "Items it changes now",
      width: "70px",
      align: "right",
      value: (a) => (a.active ? (affected.get(a.id) ?? 0) : null),
      cell: (a) => <span className={off(a, "text-muted")}>{a.active ? fmt(affected.get(a.id) ?? 0) : "—"}</span>,
    },
    {
      key: "active",
      header: "On",
      width: "56px",
      value: (a) => (a.active ? 0 : 1),
      text: (a) => (a.active ? "yes" : ""),
      cell: (a) => (
        <input
          type="checkbox"
          checked={a.active}
          disabled={!editable}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => update(a.id, { active: e.target.checked })}
          className="accent-[var(--accent)]"
        />
      ),
    },
    {
      key: "remove",
      header: "",
      width: "36px",
      value: () => "",
      cell: (a) =>
        can("forecast_adjustment.remove") && (
          <button
            type="button"
            title="Remove"
            className="rounded p-1 text-faint hover:text-danger"
            onClick={(e) => {
              e.stopPropagation();
              mutate("forecast_adjustment.remove", { id: a.id });
            }}
          >
            <Trash2 className="size-3.5" />
          </button>
        ),
    },
  ];
  const likeColumns: Column<ItemPlan>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(110px,1fr)",
      value: (p) => itemOf.get(p.id)?.sku,
      cell: (p) => <span className="font-mono">{itemOf.get(p.id)?.sku}</span>,
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(160px,2fr)",
      value: (p) => itemOf.get(p.id)?.name,
      cell: (p) => <span className="truncate text-muted">{itemOf.get(p.id)?.name}</span>,
    },
    {
      key: "like",
      header: "Like",
      width: "minmax(160px,1.5fr)",
      value: (p) => itemOf.get(p.like_item_id ?? "")?.sku,
      text: (p) => `like ${itemOf.get(p.like_item_id ?? "")?.sku ?? ""} at ${fmt(Number(p.like_pct))}%`,
      cell: (p) => (
        <span className="truncate">
          like <span className="font-mono">{itemOf.get(p.like_item_id ?? "")?.sku}</span> at {fmt(Number(p.like_pct))}%
        </span>
      ),
    },
    {
      key: "next30",
      header: "Next 30 days",
      width: "110px",
      align: "right",
      value: (p) => Math.round(forecasts.byItem.get(p.id)?.next30 ?? 0),
      cell: (p) => fmt(Math.round(forecasts.byItem.get(p.id)?.next30 ?? 0)),
    },
  ];
  return (
    <div className="mx-auto max-w-5xl space-y-6 p-5">
      <ChartCard
        title="Scoped adjustments"
        subtitle="A percentage on top of the forecast for some items and months: a launch, a sale, a slow vendor. Several that match multiply; a month's override replaces them."
        views={adjustments.length ? setAdjustSlot : undefined}
      >
        {editable && <NewAdjustment />}
        {!adjustments.length ? (
          <div className="py-2 text-muted">None yet.</div>
        ) : (
          <div className="mt-3">
            <DataTable
              fit
              rows={ordered}
              columns={adjustColumns}
              defaultSort={{ key: "active" }}
              editing={editable}
              rowNoun={["adjustment", "adjustments"]}
              exportName="Scoped adjustments"
              viewsSlot={adjustSlot}
              pageFilters={false}
            />
          </div>
        )}
      </ChartCard>
      <ChartCard
        title="Forecast like"
        subtitle="New items forecast from another item's sales and seasonality, scaled. Set it in an item's Forecast section."
        views={likes.length ? setLikeSlot : undefined}
      >
        {!likes.length ? (
          <div className="py-2 text-muted">None.</div>
        ) : (
          <DataTable
            fit
            rows={likes}
            columns={likeColumns}
            activeId={openId}
            onOpen={(p) => setSearch({ item: p.id })}
            rowNoun={["item", "items"]}
            exportName="Forecast like"
            viewsSlot={likeSlot}
            pageFilters={false}
          />
        )}
      </ChartCard>
    </div>
  );
}

function NewAdjustment() {
  const items = useRows("item");
  const vendors = useRows("vendor");
  const projects = useRows("project");
  const products = useRows("shopify_product");
  const [draft, setDraft] = useState({ scope: "project" as (typeof ADJUSTMENT_SCOPES)[number], value: "", pct: "", from: "", to: "", note: "" });
  const valueOptions = useMemo(() => {
    const sorted = (list: { value: string; label: string }[]) => [{ value: "", label: "Pick…" }, ...list.sort((a, b) => a.label.localeCompare(b.label))];
    switch (draft.scope) {
      case "vendor":
        return sorted(vendors.filter((v) => !v.archived).map((v) => ({ value: v.id, label: v.short_name || v.name })));
      case "project":
        return sorted(projects.map((p) => ({ value: p.id, label: p.name })));
      case "product_type":
        return sorted([...new Set(products.map((p) => p.product_type).filter(Boolean))].map((t) => ({ value: t, label: t })));
      case "item":
        return sorted(items.filter((i) => i.kind === "sell" && !i.archived).map((i) => ({ value: i.id, label: `${i.sku} · ${i.name}` })));
      default:
        return null;
    }
  }, [draft.scope, vendors, projects, products, items]);
  const add = async () => {
    const pct = numberOf(draft.pct.replace(/%/g, "").replace(/^\s*\+/, "")); // "+20", "−30%", or a sum
    if (!draft.pct || !Number.isFinite(pct) || pct <= -100) return toast.error("Enter a percentage, e.g. 20 or −30");
    const ok = await mutate("forecast_adjustment.create", {
      id: crypto.randomUUID(),
      scope: draft.scope,
      scope_value: draft.value,
      pct,
      month_from: draft.from ? `${draft.from}-01` : null,
      month_to: draft.to ? `${draft.to}-01` : null,
      note: draft.note.trim(),
    });
    if (ok) setDraft({ ...draft, value: "", pct: "", note: "" });
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md bg-surface-2 p-2 text-[12px]">
      <div className="w-36">
        <Select
          value={draft.scope}
          options={ADJUSTMENT_SCOPES.map((s) => ({ value: s, label: ADJUSTMENT_SCOPE_LABELS[s] }))}
          onChange={(e) => setDraft({ ...draft, scope: e.target.value as (typeof ADJUSTMENT_SCOPES)[number], value: "" })}
        />
      </div>
      {valueOptions ? (
        <div className="w-56">
          <Select value={draft.value} options={valueOptions} onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
        </div>
      ) : draft.scope === "tag" ? (
        <div className="w-40">
          <Input value={draft.value} placeholder="Tag" onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
        </div>
      ) : null}
      {/* Inputs fill their box, so each sits in a sized one. */}
      <div className="w-24">
        <Input value={draft.pct} placeholder="+20 or −30" onChange={(e) => setDraft({ ...draft, pct: e.target.value })} className="text-right font-mono" />
      </div>
      <span className="text-muted">% from</span>
      <div className="w-36">
        <Input type="month" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
      </div>
      <span className="text-muted">to</span>
      <div className="w-36">
        <Input type="month" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
      </div>
      <div className="min-w-40 flex-1">
        <Input
          value={draft.note}
          placeholder="Why (launch, sale…)"
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
      </div>
      <Button variant="primary" size="sm" onClick={add}>
        Add
      </Button>
    </div>
  );
}

// Detail panel -----------------------------------------------------------------------------------------------

function ForecastDetail({ id }: { id: string }) {
  const item = useRow("item", id);
  if (!item) return null;
  return (
    <PlanDetail item={item} className="w-[480px]">
      <ForecastSection item={item} open />
      <ItemPlanSection item={item} />
      <SalesSection item={item} />
    </PlanDetail>
  );
}
